import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Mail, BookOpen } from "lucide-react";

import "../../styles/marketing.css";
import Seo, { breadcrumbJsonLd } from "../marketing/Seo";
import MarketingNav from "../marketing/MarketingNav";
import MarketingFooter from "../marketing/MarketingFooter";
import Breadcrumbs from "../marketing/Breadcrumbs";

const useActiveSection = (ids) => {
  const [active, setActive] = useState(ids[0]);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    );
    ids.forEach((id) => { const el = document.getElementById(id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [ids]);
  return active;
};

const PrevNext = ({ prev, next, categoryId }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8 pt-6 border-t border-slate-100">
    {prev ? (
      <Link
        to={`/docs/${categoryId}#${prev.id}`}
        className="flex items-center gap-3 group rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-blue-200 hover:shadow-sm"
        style={{ textDecoration: "none" }}
        data-testid={`prev-article-${prev.id}`}
      >
        <ArrowLeft className="w-4 h-4 shrink-0 text-[var(--mk-blue)] group-hover:-translate-x-0.5 transition-transform" />
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-400 m-0">Previous</p>
          <p className="text-[14px] font-medium text-slate-900 truncate m-0">{prev.title}</p>
        </div>
      </Link>
    ) : <div />}
    {next ? (
      <Link
        to={`/docs/${categoryId}#${next.id}`}
        className="flex items-center gap-3 group rounded-xl border border-slate-200 bg-white p-4 transition-all hover:border-blue-200 hover:shadow-sm sm:text-right justify-end"
        style={{ textDecoration: "none" }}
        data-testid={`next-article-${next.id}`}
      >
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.12em] font-semibold text-slate-400 m-0">Next</p>
          <p className="text-[14px] font-medium text-slate-900 truncate m-0">{next.title}</p>
        </div>
        <ArrowRight className="w-4 h-4 shrink-0 text-[var(--mk-blue)] group-hover:translate-x-0.5 transition-transform" />
      </Link>
    ) : <div />}
  </div>
);

const DocsCategoryLayout = ({ category, articles }) => {
  const ids = articles.map((a) => a.id);
  const active = useActiveSection(ids);
  const Icon = category.icon;

  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const el = document.getElementById(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, []);

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Docs", path: "/docs" },
    { name: category.name, path: `/docs/${category.id}` },
  ];

  return (
    <div className="mk min-h-screen overflow-x-hidden">
      <Seo
        title={`${category.name} — Fieldz AI Documentation`}
        description={category.intro || `${category.name} guides for Fieldz AI field service management software.`}
        path={`/docs/${category.id}`}
        jsonLd={breadcrumbJsonLd(crumbs)}
      />
      <MarketingNav />

      {/* Hero */}
      <div className="pt-24 md:pt-32 pb-8 bg-gradient-to-b from-blue-50/70 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-5 lg:px-8">
          <div className="mb-6">
            <Breadcrumbs items={crumbs} />
          </div>
          <div className="flex items-start gap-4">
            {Icon && (
              <div className="w-14 h-14 rounded-2xl bg-[var(--mk-blue-light)] border border-blue-100 flex items-center justify-center shrink-0">
                <Icon className="w-6 h-6 text-[var(--mk-blue)]" />
              </div>
            )}
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[var(--mk-blue)] mb-2 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> Docs
              </p>
              <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-2" data-testid={`docs-${category.id}-title`}>
                {category.name}
              </h1>
              {category.intro && (
                <p className="text-[15px] leading-[1.7] text-slate-600 max-w-2xl">{category.intro}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-[1200px] mx-auto px-5 lg:px-8 pb-20 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside className="hidden lg:block">
          <nav className="mk-toc" aria-label="Article list">
            <h2 className="mk-toc-title">In this category</h2>
            <ul className="mk-toc-list">
              {articles.map((a, i) => (
                <li key={a.id}>
                  <a href={`#${a.id}`} className={active === a.id ? "is-active" : ""} data-testid={`toc-${category.id}-${a.id}`}>
                    {i + 1}. {a.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="mk-prose min-w-0">
          {articles.map((article, i) => (
            <article key={article.id} id={article.id} className="scroll-mt-28">
              {i > 0 && <hr />}
              <h2 style={{ marginTop: i === 0 ? 0 : undefined }}>
                {i + 1}. {article.title}
              </h2>
              {article.body}
              <PrevNext
                prev={i > 0 ? articles[i - 1] : null}
                next={i < articles.length - 1 ? articles[i + 1] : null}
                categoryId={category.id}
              />
            </article>
          ))}

          <section className="mt-10">
            <div
              className="rounded-2xl border border-blue-200 bg-gradient-to-b from-blue-50/70 to-white p-7 shadow-[0_10px_30px_-8px_rgba(0,70,190,0.12)]"
              data-testid={`docs-${category.id}-footer-cta`}
            >
              <h2 style={{ marginTop: 0 }}>Didn&apos;t find what you need?</h2>
              <p>
                Our team will help you set up exactly what you need. We respond within 24 hours and walk you
                through any feature personally if it helps.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-5">
                <Link to="/contact" className="mk-btn mk-btn-primary" style={{ textDecoration: "none" }}>
                  <Mail className="w-4 h-4" /> Contact Support
                </Link>
                <Link to="/support" className="mk-btn mk-btn-outline" style={{ textDecoration: "none" }}>
                  Browse FAQ
                </Link>
              </div>
            </div>
          </section>
        </main>
      </div>

      <MarketingFooter />
    </div>
  );
};

export default DocsCategoryLayout;
